import { useTranslation, Trans } from 'react-i18next';
import tw from 'twin.macro';

import { useMegaphoneOnboardContext } from '@account_module/context/MegaphoneOnboardContext';
import { ONBOARDING_FORM_KEYS } from '@account_module/constants/onBoardingKeys';
import { CloseIcon } from '@icons/CloseIcon';

const SidebarWrapper = tw.div`h-full w-full border-l border-solid border-muted4 p-[24px] overflow-y-auto`;
const SidebarHeader = tw.div`w-[270px] flex items-center justify-between mb-[18px]`;
const SidebarContent = tw.div`w-[270px]`;
const SidebarTitle = tw.h3`text-left text-[20px] font-bold text-primary`;
const CopyTitle = tw.h5`text-left text-base font-bold text-primary mt-[16px] mb-[8px]`;
const P = tw.p`text-left text-text-muted-2 leading-[140%] text-base font-light mb-4`;
const SmallP = tw(P)`text-sm leading-[150%]`;
const ListItem = tw.li`text-text-muted-2 leading-[150%] text-sm font-light`;
const SubListItem = tw.li`text-text-muted-2 leading-[170%] text-sm font-light ml-6`;
const SubList = tw.ul`mt-1 list-disc`;
const CloseButton = tw.button`border border-solid border-card-border rounded`;
const InfoLink = tw.span`block text-text-muted-2 leading-[150%] text-sm font-light mt-4`;

const sidebarTitle = {
  [ONBOARDING_FORM_KEYS.API_TOKEN]: 'account_module.onboard_page.sidebar.api_key.title',
  [ONBOARDING_FORM_KEYS.ORG_ID]: 'account_module.onboard_page.sidebar.organization_id.title',
};

export const OnboardSidebar = () => {
  const { t } = useTranslation();
  const { selectedInput, setIsSidebarVisible } = useMegaphoneOnboardContext();

  const sidebarContent = {
    [ONBOARDING_FORM_KEYS.API_TOKEN]: (
      <>
        <P>{t('account_module.onboard_page.sidebar.api_key.description')}</P>
        <P>{t('account_module.onboard_page.sidebar.api_key.description_2')}</P>
        <P>
          <Trans i18nKey='account_module.onboard_page.sidebar.api_key.description_3'>
            <span tw='font-medium text-primary'>
              Talk to your company’s Megaphone administrator
            </span>{' '}
            if you need help creating or using a token
          </Trans>
        </P>
        <CopyTitle>{t('account_module.onboard_page.sidebar.api_key.tip_1.title')}</CopyTitle>
        <SmallP tw='font-semibold'>
          {t('account_module.onboard_page.sidebar.api_key.tip_1.description')}
        </SmallP>
        <ul>
          <ListItem>
            {t('account_module.onboard_page.sidebar.api_key.tip_1.list.list_item_1')}
          </ListItem>
          <ListItem>
            {t('account_module.onboard_page.sidebar.api_key.tip_1.list.list_item_2')}
          </ListItem>
          <ListItem>
            {t('account_module.onboard_page.sidebar.api_key.tip_1.list.list_item_3')}
          </ListItem>
        </ul>
        <InfoLink>
          <Trans i18nKey='account_module.onboard_page.sidebar.api_key.info_link'>
            More information about Megaphone API tokens is
            <a
              target='_blank'
              rel='noreferrer'
              href='https://intercom.help/megaphone/en/articles/2247461-megaphone-api'>
              here.
            </a>
          </Trans>
        </InfoLink>
      </>
    ),

    [ONBOARDING_FORM_KEYS.ORG_ID]: (
      <>
        <P>{t('account_module.onboard_page.sidebar.organization_id.description')}</P>
        <CopyTitle>
          {t('account_module.onboard_page.sidebar.organization_id.tip_1.title')}
        </CopyTitle>
        <ul tw='mx-0'>
          <ListItem>
            {t('account_module.onboard_page.sidebar.organization_id.tip_1.list.list_item_1')}
          </ListItem>
          <ListItem>
            {t('account_module.onboard_page.sidebar.organization_id.tip_1.list.list_item_2')}
          </ListItem>
          <ListItem>
            {t('account_module.onboard_page.sidebar.organization_id.tip_1.list.list_item_3')}
          </ListItem>
          <SubList>
            <SubListItem>
              {t(
                'account_module.onboard_page.sidebar.organization_id.tip_1.list.list_item_3_sublist.sublist_item_1'
              )}
            </SubListItem>
            <SubListItem>
              {t(
                'account_module.onboard_page.sidebar.organization_id.tip_1.list.list_item_3_sublist.sublist_item_2'
              )}
            </SubListItem>
            <SubListItem>
              {t(
                'account_module.onboard_page.sidebar.organization_id.tip_1.list.list_item_3_sublist.sublist_item_3'
              )}
            </SubListItem>
          </SubList>
        </ul>
      </>
    ),
  };

  return (
    <SidebarWrapper>
      <SidebarHeader>
        <SidebarTitle>{t(sidebarTitle[selectedInput])}</SidebarTitle>
        <CloseButton onClick={() => setIsSidebarVisible(false)}>
          <CloseIcon tw='fill-primary' />
        </CloseButton>
      </SidebarHeader>
      <SidebarContent>{sidebarContent[selectedInput]}</SidebarContent>
    </SidebarWrapper>
  );
};
